<script>
import { app } from '@/main'
import { mapActions } from 'vuex'
import { required, requiredIf, url } from 'vuelidate/lib/validators'
import { withParams } from 'vuelidate/lib/validators/common'
import { debounce } from 'lodash'
import { formatSlug } from '@/support/utils/stringHelper'
import { getRichTextContentLength } from '@/support/utils/richTextHelper'
import wizardForm from '@/mixins/wizardForm'

const RICH_TEXT_EDITOR_TOOLBAR = [
  [
    'bold',
    'italic',
    'underline',
    'strike'
  ],
  [ 'clean' ],
  [ 'link' ]
]

const richTextMaxLength = (max) => withParams({
  type: 'maxLength',
  max
}, (value) => {
  if (!value) return true

  return getRichTextContentLength(value) <= max
})

export default {
  name: 'ToolsManage',
  components: {
    Radio: () => import('@/components/general/Radio'),
    Upload: () => import('@/components/general/Upload'),
    Action: () => import('@/components/general/Action'),
    Divisor: () => import('@/components/general/Divisor'),
    Datatable: () => import('@/components/general/Datatable'),
    ActionBar: () => import('@/components/general/ActionBar'),
    InputField: () => import('@/components/general/InputField'),
    Pagination: () => import('@/components/general/Pagination'),
    FormSection: () => import('@/components/general/FormSection'),
    SelectField: () => import('@/components/general/SelectField'),
    TextareaField: () => import('@/components/general/TextareaField'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    RichTextEditor: () => import('@/components/general/RichTextEditor'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    MoneyField: () => import('@/components/general/MoneyField'),
    Tabs: () => import('@/components/general/Tabs'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm')
  },

  mixins: [ wizardForm ],

  data () {
    return {
      typesToolsList: [],
      entrepreneurProfileList: [],
      themesList: [],
      activeTab: 1,
      thematicPages: [],
      configurationList: [],
      satisfactionSurveyList: [],
      formData: {
        showThematicPages: false,
        id: null,
        idToolType: null,
        title: null,
        slug: '',
        callText: '',
        audience: '',
        differentials: '',
        description: '',
        showOnPortal: true,
        isExternalLink: false,
        exclusiveToCompany: false,
        featuredImage: null,
        cardImage: null,
        partnerUrl: null,
        objective: null,
        entrepreneurProfiles: [],
        tags: null,
        triggerWords: null,
        portfolioId: null,
        surveyEnabled: false,
        idSurvey: null,
        surveyRequired: false,
        seoTitle: null,
        seoDescription: null,
        seoKeywords: null,
        linkedThematicPages: [],
        paymentType: 'free',
        price: null,
        money: {
          decimal: ',',
          thousands: '.',
          prefix: 'R$ ',
          suffix: ' #',
          precision: 2,
          masked: false
        }
      },

      backUrl: { name: 'tools.index' },
      isTitleDuplicate: false,
      objectiveInatives: [],
      modalLeave: false,
      formatedUrl: '',
      isLoadingMoreThematicPages: false,

      thematicPagesPagination: {
        query: {
          name: null,
          limit: 300
        },

        filter: {
          active: 1
        },

        order: {
          name: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      thematicPageInfiniteScrollId: new Date()
        .getTime(),

      minimumSolutionPrice: 0.0,
      minimumSolutionPriceErrorMessage: null,
      isValidPortfolioId: true,
      isValidIntegrationCode: true,
      portfolioId: null,
      linkToToolTabActive: 0,
      linkToSolutionTab: [{ text: 'solution.manage:link.tab.2' }],

      RICH_TEXT_EDITOR_TOOLBAR,

      yesNoOptions: [
        {
          label: app.$t('global:form.tracks.yes'),
          value: true
        },
        {
          label: app.$t('global:form.tracks.no'),
          value: false
        }
      ],

      paymentTypeOptions: [
        {
          text: app.$t('global:form.payment.type.free'),
          label: app.$t('global:free'),
          value: 'free'
        },
        {
          text: app.$t('global:form.payment.type.paid'),
          label: app.$t('global:paid'),
          value: 'paid'
        }
      ]
    }
  },

  validations () {
    return {
      formData: {
        idToolType: { required },
        title: {
          required,
          titleDuplicate: () => {
            return !this.isTitleDuplicate
          }
        },

        slug: {
          required,
          regexSlug: (event) => {
            const regexSlug = new RegExp('^[a-z0-9]+(?:-[a-z0-9]+)*$')

            return regexSlug.test(event)
          }
        },

        callText: { required },
        cardImage: { required },
        objective: { required },
        partnerUrl: {
          required,
          validURL: url
        },

        description: {
          required,
          maxLength: richTextMaxLength(250)
        },

        audience: {
          required,
          maxLength: richTextMaxLength(250)
        },

        differentials: {
          required: requiredIf(function () {
            return this.formData.idToolType && this.formData.idToolType !== 1
          }),

          maxLength: richTextMaxLength(250)
        },

        featuredImage: { required },
        showOnPortal: {
          required: (event) => {
            return event !== null && event !== undefined
          }
        },

        exclusiveToCompany: {
          required: requiredIf(function () {
            return !!this.formData.showThematicPages &&
            (this.formData.exclusiveToCompany === null ||
            this.formData.exclusiveToCompany === undefined)
          })
        },

        portfolioId: {
          emptyRAE: () => {
            return !(this.formData.active && !!this.portfolioId && !this.formData.portfolioId)
          },

          valueBiggerZero: (valorDigitado) => {
            return valorDigitado ? valorDigitado > 0 : true
          },

          validatePortfolio: () => {
            return this.isValidPortfolioId
          }
        },

        surveyEnabled: {
          required: (event) => {
            return event !== null && event !== undefined
          }
        },

        idSurvey: {
          required: requiredIf(function () {
            return this.formData.surveyEnabled
          })
        },

        price: {
          required: requiredIf(function () {
            return this.formData.paymentType === 'paid'
          })
        }
      }
    }
  },

  computed: {
    availableThematicPages () {
      return this.thematicPages.filter(thematicPage => !this.formData.linkedThematicPages.find(
        findThematicPage => thematicPage.id === findThematicPage.id
      ))
    },

    isEditing () {
      return !!(this.$route.params.id || this.formData.id || false)
    }

  },

  watch: {
    'formData.showThematicPages' (value) {
      if (value) return

      this.formData.linkedThematicPages = []
    },

    'formData.title' () {
      if (!this.$v.formData.slug.$dirty && !this.isEditing) {
        this.formData.slug = formatSlug(this.formData.title)
      }

      if (!this.isTitleDuplicate) return
      this.isTitleDuplicate = false
    },

    'formData.slug' () {
      this.formatedUrl = `${process.env.VUE_APP_PORTAL_URL}ferramenta/${this.formData.slug}`
    },

    'formData.price' (value) {
      this.minimumSolutionPriceErrorMessage = value < this.minimumSolutionPrice ? 'Esse valor é menor do que a parcela mínima' : null
    },

    'formData.paymentType' (value) {
      if (value === 'free' && this.formData.price === 0.0) {
        this.minimumSolutionPriceErrorMessage = null
      }
    },

    'activeTab' (value) {
      if (value === 3 && this.formData.active && this.portfolioId) {
        this.validatePortfolio()
      }
    }
  },

  mounted () {
    if (this.$route.params.isNew) {
      this.activeTab = 2
    } else if (this.$route.params.tab) {
      this.activeTab = this.$route.params.tab
    }
  },

  created () {
    this.setup()
      .then(() => {
        if (this.isEditing) {
          this.setFetching(true)

          this.attemptManageTool(this.$route.params.id)
            .then(() => {
              this.formData = {
                ...this.formData,
                ...this.$store.getters.getManageTool
              }

              this.formData.idToolType = this.formData.toolType.id
              this.formData.objective = []
              this.objectiveInatives = []

              this.formData.toolTypeObjective.forEach(data => {
                if (data.theme.active) {
                  this.formData.objective.push(data.theme.alias)
                } else {
                  this.objectiveInatives.push(data.theme.alias)
                }
              })

              if (this.formData.price) {
                this.formData.paymentType = 'paid'
              }

              if (this.formData.idSurvey) {
                this.formData.surveyEnabled = true
              }

              if (this.formData.thematicPages && this.formData.thematicPages.length > 0) {
                this.formData.showThematicPages = true

                this.formData.linkedThematicPages = this.formData.thematicPages.map(thematicPage => {
                  return {
                    id: thematicPage.id,
                    name: thematicPage.name
                  }
                })
              }

              if (this.formData.toolEntrepreneurProfile && this.formData.toolEntrepreneurProfile.length > 0) {
                this.formData.entrepreneurProfiles = this.formData.toolEntrepreneurProfile.map(item => {
                  return item.entrepreneurProfile.id
                })
              }

              this.formData.isExternalLink = !!this.formData.externalLink

              this.portfolioId = this.formData.portfolioId
              this.$v.$touch()
            })
            .catch(() => {
              this.setFeedback({ message: this.$t('type_tool.load:fetch.error') })
              this.$router.push(this.backUrl)
            })
            .finally(() => {
              this.$nextTick(() => {
                this.snapshotWizardState()
              })
              this.setFetching(false)
            })
        } else {
          this.$nextTick(() => {
            this.snapshotWizardState()
          })
        }

        this.$v.$reset()
      })
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptManageTool',
      'attemptSaveTool',
      'attemptUpdateTool',
      'attemptGetTypeToolsList',
      'attemptGetThemesList',
      'attemptGetEntrepreneurProfilesList',
      'attemptGetThematicPagesList',
      'attemptValidatePortfolio',
      'attemptGetSurveysList'
    ]),

    fillSeoData () {
      if (this.activeTab === 4) {
        this.formData.seoTitle = this.formData.seoTitle ? this.formData.seoTitle : this.formData.title

        this.formData.seoDescription = this.formData.seoDescription
          ? this.formData.seoDescription
          : this.formData.callText
      }
    },

    getValidationFieldsByTab () {
      return {
        1: [
          this.$v.formData.idToolType,
          this.$v.formData.title,
          this.$v.formData.slug,
          this.$v.formData.callText,
          this.$v.formData.description,
          this.$v.formData.audience,
          this.$v.formData.differentials,
          this.$v.formData.objective,
          this.$v.formData.partnerUrl,
          this.$v.formData.featuredImage,
          this.$v.formData.cardImage,
          this.$v.formData.price,
          this.$v.formData.showOnPortal,
          this.$v.formData.exclusiveToCompany,
          this.$v.formData.surveyEnabled,
          this.$v.formData.idSurvey
        ],
        2: [],
        3: [ this.$v.formData.portfolioId ],
        4: [],
        5: []
      }
    },

    prevPage () {
      if (this.activeTab > 1) this.activeTab -= 1
    },

    nextPage () {
      if (!this.validateActiveTab()) {
        this.setFeedback({ message: this.$t('management.certificate:feedback.validation.error') })

        return
      }

      if (this.activeTab < 5) this.activeTab += 1

      if (this.activeTab === 4) {
        this.fillSeoData()
      }
    },

    formatFormData () {
      const data = {}

      data.title = this.formData.title
      data.id = this.formData.id
      data.idToolType = this.formData.idToolType
      data.slug = this.formData.slug
      data.description = this.formData.description
      data.callText = this.formData.callText
      data.partnerUrl = this.formData.partnerUrl
      data.objectives = this.formData.objective
      data.tags = this.formData.tags || ''
      data.audience = this.formData.audience
      data.differentials = this.formData.differentials
      data.price = this.formData.paymentType === 'paid' ? this.formData.price : null
      data.payment_type = this.formData.paymentType === 'paid' ? 'paid' : 'free'
      data.featuredImage = this.formData.featuredImage
      data.cardImage = this.formData.cardImage
      data.portfolio_id = this.formData.portfolioId || null
      data.showOnPortal = this.formData.showOnPortal ? 1 : 0
      data.idSurvey = this.formData.surveyEnabled ? this.formData.idSurvey : null
      data.isExternalLink = this.formData.isExternalLink ? 1 : 0
      data.exclusiveToCompany = this.formData.exclusiveToCompany ? 1 : 0
      data.tags = this.formData.tags
      data.seoTitle = this.formData.seoTitle
      data.seoDescription = this.formData.seoDescription
      data.seoKeywords = this.formData.seoKeywords
      data.entrepreneurProfiles = this.formData.entrepreneurProfiles
      data.thematicPages = []

      this.formData.linkedThematicPages.forEach(thematicPage => {
        data.thematicPages.push(thematicPage.id)
      })

      return data
    },

    submitCreation () {
      this.setFetching(true)

      return this.attemptSaveTool(this.formatFormData())
        .then(({ data }) => {
          this.formData.id = data.id
          this.snapshotWizardState()
          this.setFeedback({ message: this.$t('tool:submit.create.success') })
        })
        .catch(({ data }) => {
          if (data.error.message === 'tool_title_already_in_use') {
            this.setFeedback({ message: this.$t('tool:submit.create.title.already.exists.error') })

            this.isTitleDuplicate = true
          } else {
            this.setFeedback({ message: this.$t('tool:submit.create.error') })
          }
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    addToLinkedThematicPage (thematicPageId) {
      const thematicPage = this.thematicPages.find(thematicPage => thematicPage.id === thematicPageId)

      thematicPage.active = 1
      this.formData.linkedThematicPages.push(thematicPage)
    },

    removeLinkedThematicPage (thematicPageId) {
      this.formData.linkedThematicPages = this.formData.linkedThematicPages.filter(
        thematicPage => thematicPage.id !== thematicPageId
      )
    },

    clearLinkedThematicPages (event) {
      if (event) return

      this.formData.linkedThematicPages = this.formData.linkedThematicPages.filter(linkedThematicPage =>
        linkedThematicPage.active !== 1
      )
    },

    submitUpdate () {
      this.setFetching(true)

      return this.attemptUpdateTool(this.formatFormData())
        .then(() => {
          this.snapshotWizardState()
          this.setFeedback({ message: this.$t('tool:submit.update.success') })
        })
        .catch(({ data }) => {
          if (data.error.message === 'tool_title_already_in_use') {
            this.setFeedback({ message: this.$t('tool:submit.create.title.already.exists.error') })

            this.isTitleDuplicate = true
            this.$v.formData.slug.$touch()
          } else {
            this.setFeedback({ message: this.$t('tool:submit.update.error') })
          }
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    submit () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        return (this.isEditing ? this.submitUpdate() : this.submitCreation())
          .then(() => true)
      }

      this.setFeedback({ message: this.$t('thematicPages:feedback.validation.error') })
      this.activeTab = this.getFirstInvalidTab() || this.activeTab

      return Promise.resolve(false)
    },

    leave () {
      this.$router.push(this.backUrl)
    },

    getTypeToolList () {
      this.attemptGetTypeToolsList({
        filter: {
          active: true
        },
        page: 1,
        limit: 100
      })
        .then(pagination => {
          if (pagination.data) {
            pagination.data.data.forEach(element => {
              this.typesToolsList.push({
                text: element.title,
                value: element.id
              })
            })

            return true
          }
        })
    },

    searchThematicPage (search) {
      this.thematicPagesPagination.query.name = search
      this.thematicPagesPagination.page = 1
      this.thematicPagesPagination.lastPage = null
      this.fetchThematicPagesData()
    },

    loadMoreThematicPages () {
      this.isLoadingMoreThematicPages = true

      if (this.thematicPagesPagination.lastPage) {
        if (this.thematicPagesPagination.lastPage > this.thematicPagesPagination.page) {
          this.thematicPagesPagination.page += 1
        } else {
          this.isLoadingMoreThematicPages = false

          return
        }
      }

      this.fetchThematicPagesData(true)
        .then(() => {
          this.isLoadingMoreThematicPages = false
        })
    },

    async fetchThematicPagesData (isLoadMore = false) {
      return this.attemptGetThematicPagesList(this.thematicPagesPagination)
        .then((pagination) => {
          this.thematicPageInfiniteScrollId += 1
          this.thematicPagesPagination.lastPage = pagination.lastPage
          this.thematicPagesPagination.page = pagination.actualPage

          if (pagination.data) {
            if (!isLoadMore) {
              this.thematicPages = []
            }

            pagination.data.forEach(element => {
              this.thematicPages.push({
                name: element.name,
                id: element.id,
                thematicPageLearningPaths: element.thematicPageLearningPaths,
                thematicPageSolutions: element.thematicPageSolutions
              })
            })
          }

          return pagination
        })
    },

    getThemesList () {
      return this.attemptGetThemesList({})
        .then(pagination => {
          if (pagination.data) {
            pagination.data.forEach(element => {
              this.themesList.push({
                text: element.name,
                value: element.alias
              })
            })

            return true
          }
        })
    },

    getEntrepreneurProfilesList () {
      return this.attemptGetEntrepreneurProfilesList({})
        .then(pagination => {
          if (pagination.data) {
            pagination.data.forEach(element => {
              this.entrepreneurProfileList.push({
                text: element.name,
                value: element.id
              })
            })

            return true
          }
        })
    },

    setup () {
      this.setFetching(true)

      return Promise.all([
        this.getTypeToolList(),
        this.getThemesList(),
        this.getEntrepreneurProfilesList(),
        this.attemptGetSurveysList({
          filter: { active: 1 },
          page: 1,
          limit: 100
        })
          .then(pagination => {
            if (pagination.data) {
              pagination.data.forEach(element => {
                if (element.type === 'satisfaction') {
                  this.satisfactionSurveyList.push({
                    text: element.name,
                    value: element.id
                  })
                }
              })

              return true
            }
          })
      ])
        .finally(() => {
          this.setFetching(false)
        })
    },

    validatePortfolio: debounce(async function () {
      if (this.formData.portfolioId) {
        this.setFetching(true)


        this.isValidPortfolioId = await this.checkPortfolioCode() || await this.checkPortfolioCode()

        this.$v.$touch()
        this.setFetching(false)
      } else {
        this.isValidPortfolioId = true
      }
    }, 500),

    checkPortfolioCode () {
      return this.attemptValidatePortfolio(this.formData.portfolioId)
        .then((response) => !!response.data)
        .catch(() => false)
    }
  }
}
</script>

<template>
  <div class="main-content solutions-create">
    <ContentHeader
      :title="isEditing ? formData.title : $t('tools.create:header.title')"
      dark-theme
      sticky
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back.tools')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="leave"
      />
      <ActionBar slot="actionbar" />
      <template slot="buttons">
        <Action
          :text="$t('global:form.save')"
          type="button"
          flat
          dark
          @click="submit(false)"
        />
      </template>
    </ContentHeader>
    <div
      v-show="activeTab === 1"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 1}) }}
          </h2>
          <p
            class="solutions-create-description"
            v-html="$t('tools.create:header.description.1')"
          />
          <span
            v-if="formData.active"
            class="td-tag active-ribbon"
          >{{ $t('global.status:active') }}</span>
          <span
            v-else
            class="td-tag inactive-ribbon"
          >{{ $t('global.status:inactive') }}</span>
        </div>
        <form
          class="form"
          @submit.prevent="submit"
        >
          <InputField
            v-model="formData.title"
            :label="$t('global:form.tool.name')"
            :validation="$v.formData.title"
            :counter="100"
          />

          <InputField
            v-model="formData.slug"
            :label="$t('global:form.tool.slug')"
            :under-description="formatedUrl"
            :validation="$v.formData.slug"
            :counter="100"
          />

          <SelectField
            v-model="formData.idToolType"
            tabindex="0"
            :label="$t('global:form.tool.type')"
            :items="typesToolsList"
            :validation="$v.formData.idToolType"
            :under-description="$t('global:form.tool.type.under-description')"
          />

          <InputField
            v-model="formData.callText"
            :label="$t('global:form.solutions.callText')"
            :validation="$v.formData.callText"
            :counter="250"
            :under-description="$t('global:form.tool.callText.under-description')"
          />

          <FormSection :title="$t('global:form.solutions.description')">
            <RichTextEditor
              v-model="formData.description"
              :validation="$v.formData.description"
              :toolbar="RICH_TEXT_EDITOR_TOOLBAR"
              :max-length="250"
              class="rich-text"
            />
          </FormSection>

          <FormSection :title="$t('global:form.solutions.audience')">
            <RichTextEditor
              v-model="formData.audience"
              :toolbar="RICH_TEXT_EDITOR_TOOLBAR"
              :validation="$v.formData.audience"
              :max-length="250"
              class="rich-text"
            />
          </FormSection>

          <FormSection
            v-if="formData.idToolType && formData.idToolType !== 1"
            :title="$t('global:form.solutions.differentials')"
          >
            <RichTextEditor
              v-model="formData.differentials"
              :validation="$v.formData.differentials"
              :toolbar="RICH_TEXT_EDITOR_TOOLBAR"
              :max-length="250"
              class="rich-text"
            />
          </FormSection>

          <InputField
            v-model="formData.partnerUrl"
            :label="$t('global:form.tool.partnerUrl')"
            :validation="$v.formData.partnerUrl"
            :counter="300"
          />

          <SelectField
            v-model="formData.objective"
            :label="$t('global:form.tool.objectives')"
            :under-description="$t('global:form.tool.objectives.under-description')"
            multiple
            :items="themesList"
            :validation="$v.formData.objective"
          />

          <FormSection :title="$t('global:upload.add.image.spotlight')">
            <Upload
              v-model="formData.featuredImage"
              icon="image-multiple"
              :label="$t('global:upload.add.image')"
              :description="$t('global:upload.add.image.spotlight.description')"
              cropper
              :width="2160"
              :height="675"
              :preview="0.22"
              :validation="$v.formData.featuredImage"
            />
          </FormSection>

          <FormSection :title="$t('global:upload.add.image.card')">
            <Upload
              v-model="formData.cardImage"
              icon="image-multiple"
              :label="$t('global:upload.add.image')"
              :description="$t('global:upload.add.tools.card.description')"
              cropper
              :width="365"
              :height="220"
              :preview="0.8"
              export-format="png"
              :export-max-size="1560"
              :validation="$v.formData.cardImage"
            />
          </FormSection>

          <FormSection :title="$t('global:form.solution.investment')">
            <Radio
              key="radio-payment-type"
              v-model="formData.paymentType"
              :under-description="$t('tool.manage:field.description.investment')"
              :items="paymentTypeOptions"
              horizontal
              :validation="$v.formData.paymentType"
            />
          </FormSection>

          <FormSection
            v-show="formData.paymentType === 'paid'"
            :title="$t('global:form.solution.price')"
          >
            <MoneyField
              v-model="formData.price"
              :label="$t('global:form.price')"
              prefix="R$ "
              :validation="$v.formData.price"
            />
            <span
              v-if="minimumSolutionPriceErrorMessage"
              class="solution-price-error-message"
            >{{ minimumSolutionPriceErrorMessage }}</span>
          </FormSection>

          <FormSection :title="$t('global:form.solution.showOnPortal')">
            <Radio
              key="radio-show-on-portal"
              v-model="formData.showOnPortal"
              :under-description="$t('tool.manage:field.description.show.portal')"
              :items="yesNoOptions"
              horizontal
              :validation="$v.formData.showOnPortal"
            />
          </FormSection>

          <FormSection :title="$t('global:form.solution.externalLink')">
            <Radio
              key="radio-external-link"
              v-model="formData.isExternalLink"
              :under-description="$t('tool.manage:field.description.external.link')"
              :items="yesNoOptions"
              horizontal
            />
          </FormSection>

          <FormSection :title="$t('global:form.solution.exclusiveToCompany')">
            <Radio
              key="radio-exclusive-to-company"
              v-model="formData.exclusiveToCompany"
              :under-description="$t('tool.manage:field.description.exclusiveToCompany')"
              :items="yesNoOptions"
              horizontal
              :validation="$v.formData.exclusiveToCompany"
            />
          </FormSection>

          <FormSection :title="$t('global:form.has.survey')">
            <Radio
              key="radio-survey-enabled"
              v-model="formData.surveyEnabled"
              :under-description="$t('tool.manage:field.description.has.survey')"
              :items="yesNoOptions"
              horizontal
              :validation="$v.formData.surveyEnabled"
            />
            <SelectField
              v-if="formData.surveyEnabled"
              v-model="formData.idSurvey"
              :label="$t('global:form.survey.type')"
              :items="satisfactionSurveyList"
              :validation="$v.formData.idSurvey"
            />
          </FormSection>
        </form>
      </div>
    </div>
    <div
      v-show="activeTab === 2"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 2}) }}
          </h2>
          <p
            class="solutions-create-description"
            v-html="$t('tools.create:header.description.2')"
          />
          <span
            v-if="formData.active"
            class="td-tag active-ribbon"
          >{{ $t('global.status:active') }}</span>
          <span
            v-else
            class="td-tag inactive-ribbon"
          >{{ $t('global.status:inactive') }}</span>
        </div>
        <form
          class="form"
          @submit.prevent="submit"
        >
          <FormSection :title="$t('global:form.profiles')">
            <SelectField
              v-model="formData.entrepreneurProfiles"
              :label="$t('global:form.profile')"
              multiple
              :items="entrepreneurProfileList"
            />
          </FormSection>
          <FormSection :title="$t('global:form.solutions.tags')">
            <TextareaField
              v-model="formData.tags"
              :label="$t('global:form.solutions.tags')"
              :validation="$v.formData.tags"
              auto-grow
              :counter="255"
            />
          </FormSection>
        </form>
      </div>
    </div>
    <div
      v-show="activeTab === 3"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 3}) }}
          </h2>
          <p
            class="solutions-create-description"
            v-html="$t('tools.create:header.description.3')"
          />
          <span class="under-description">{{ $t('tools.create:header.description.3.under-description') }}</span>
          <div>
            <span
              v-if="formData.active"
              class="td-tag active-ribbon"
            >{{ $t('global.status:active') }}</span>
            <span
              v-else
              class="td-tag inactive-ribbon"
            >{{ $t('global.status:inactive') }}</span>
          </div>
        </div>
        <form
          class="form"
          @submit.prevent="submit"
        >
          <InputField
            v-model="formData.portfolioId"
            :under-description="$t('solution.manage:field.description.sebrae.code')"
            type="number"
            :min="1"
            :label="$t('global:form.solutions.portfolio.id')"
            :validation="$v.formData.portfolioId"
            hide-optional
            @keyup="validatePortfolio"
          />
        </form>
      </div>
    </div>
    <div
      v-show="activeTab === 4"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 4}) }}
          </h2>
          <p
            class="solutions-create-description"
            v-html="$t('tools.create:header.description.4')"
          />
          <span class="under-description">{{ $t('tools.create:header.description.4.under-description') }}</span>
          <div>
            <span
              v-if="formData.active"
              class="td-tag active-ribbon"
            >{{ $t('global.status:active') }}</span>
            <span
              v-else
              class="td-tag inactive-ribbon"
            >{{ $t('global.status:inactive') }}</span>
          </div>
        </div>
        <form
          class="form"
          @submit.prevent="submit"
        >
          <InputField
            v-model="formData.seoTitle"
            :label="$t('global:form.solutions.seo.title')"
            hide-optional
          />
          <InputField
            v-model="formData.seoDescription"
            :label="$t('global:form.solutions.seo.description')"
            hide-optional
          />
          <InputField
            v-model="formData.seoKeywords"
            :label="$t('global:form.solutions.seo.keywords')"
            :hint="$t('global:form.solutions.seo.keywords.hint')"
            hide-optional
          />
        </form>
      </div>
    </div>
    <div
      v-show="activeTab === 5"
      key="Tab de vínculos"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 5}) }}
          </h2>
          <p
            class="solutions-create-description"
            v-html="$t('tools.create:header.description.5')"
          />
          <span
            v-if="formData.active"
            class="td-tag active-ribbon"
          >{{ $t('global.status:active') }}</span>
          <span
            v-else
            class="td-tag inactive-ribbon"
          >{{ $t('global.status:inactive') }}</span>
        </div>
        <form
          class="center"
          @submit.prevent="submit"
        >
          <Tabs
            slot="tabs"
            :links="linkToSolutionTab"
            :index-active="linkToToolTabActive"
            @tabChange="linkToToolTabActive = $event"
          />
          <div
            v-show="linkToToolTabActive === 0"
            class="link-selection"
          >
            <FormSection
              :title="$t('show.in.Themes')"
              class="form-section--centered"
            >
              <Radio
                key="radio-show-thematic-pages"
                v-model="formData.showThematicPages"
                :items="yesNoOptions"
                horizontal
                @input="clearLinkedThematicPages"
              />
            </FormSection>

            <FormSection
              v-show="false"
              :title="$t('global:form.solution.exclusiveToThematicPages')"
              class="form-section--centered"
            >
              <Radio
                key="radio-exclusive-to-thematic-pages"
                v-model="formData.exclusiveToThematicPagesEnabled"
                :items="yesNoOptions"
                horizontal
                :validation="$v.formData.exclusiveToThematicPagesEnabled"
              />
            </FormSection>

            <div
              v-if="formData.showThematicPages"
              class="link-table"
            >
              <div class="linkable-items">
                <Datatable
                  :is-loading-more="isLoadingMoreThematicPages"
                  :items="availableThematicPages"
                  hide-horizontal-line
                  has-infinite-scroll
                  has-fade
                  empty-message="no_result"
                  @loadMore="loadMoreThematicPages"
                >
                  <template slot="thead">
                    <th
                      class="th"
                      width="300"
                    >
                      {{ $t('themes.list:datatable.header.1') }}
                    </th>
                    <th
                      class="th"
                      width="40"
                    />
                  </template>

                  <template slot="tsearch">
                    <FilterListSearch
                      :prepend-label="$t('global:form.theme-search')"
                      :hide-error-details="true"
                      class="filter-search"
                      @search="searchThematicPage"
                    />
                  </template>

                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="props.item.id"
                      @click="addToLinkedThematicPage(props.item.id)"
                    >
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small">{{ props.item.thematicPageLearningPaths.length }} {{ props.item.thematicPageLearningPaths.length === 1 ? 'Trilha' : 'Trilhas' }} | {{ props.item.thematicPageSolutions.length }} {{ props.item.thematicPageSolutions.length === 1 ? 'Solução' : 'Soluções' }}</span>
                      </td>

                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="keyboard_arrow_right"
                          flat
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
              <Divisor orientation="vertical" />
              <div class="linked-items">
                <Datatable
                  has-fade
                  :items="formData.linkedThematicPages"
                  hide-horizontal-line
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t('themes.list:datatable.header.2') }}
                    </th>
                    <th
                      class="th"
                      width="40"
                    />
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr :key="props.item.id">
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="close"
                          flat
                          @click="removeLinkedThematicPage(props.item.id)"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <Pagination
      class="center"
      :active-first-last="false"
      :active-page="activeTab"
      :page-count="5"
      @nextPage="nextPage"
      @previousPage="prevPage"
    />
    <ModalConfirm
      :active="modalLeaveOpen"
      :title="$t('global:form.leave.title')"
      :description="$t('global:form.leave.description')"
      @confirmAction="confirmWizardLeave"
      @cancelAction="cancelWizardLeave"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/solutions.css"></style>

<style>
.link-selection,
.link-selection .form-section {
  display: flex;
  margin: 40px auto 32px;
  flex-direction: column;
}

.link-selection .form-section + .form-section {
  margin-top: 0;
}

.link-selection .form-section {
  width: 480px;
}

.link-selection .form-section.form-section--centered {
  text-align: center;
}

.solutions-create-header + .center .tabs {
  display: flex;
  justify-content: center;
  margin: 65px 0 80px;
}
.warning-service-integration {
  padding: 10px;
  background-color: #ffc14d;
  margin-bottom: 10px;
}
.warning-rae-configuration {
  color: #fa6666;
  font-size: 12px;
}

.pragmatic-list {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.pragmatic-tools {
  padding-right: 15px;
}

.pragmatic-tools button {
  padding: 4px;
}

.td-tag.active-ribbon {
  background: #beffbe;
}

.td-tag.inactive-ribbon {
  background: #ffadad;
}

.td-tag {
  display: inline-block;
  vertical-align: middle;
  font-size: 1.2em;
  color: rgba(0,0,0,0.5);
  line-height: 30px;
  padding: 0 10px;
  min-width: 70px;
  text-align: center;
  background: #F3F4F7;
  border-radius: 50px;
  text-transform: none;
}

.under-description {
  display: inline-block;
  font-size: 1.2em;
  color: rgba(0,0,0,0.5);
  padding: 5px 0;
  max-width: 480px;
  text-transform: none;
}

.form-input-file-label-text,
.form-section-title {
  text-transform: none;
}

.btn.view-file {
  font-size: 16px;
  text-transform: none;
}

.solution-price-error-message {
  color: #ffc14d;
  font-size: 14px;
}
</style>
